import React, { useState } from 'react';
import styled from 'styled-components';
import { DraggablePanel } from '@repo/ui';
import {
  useDeveloperStore,
  useCameraHistory,
  useMapOriginHistory,
  useCurrentCamera,
  useCurrentMapOrigin,
  useCameraSource,
  useMapOriginSource,
  ConfigurationSource,
} from '@repo/front-end-state';
import type { ConfigurationEntry } from '@repo/front-end-state';

const DeveloperConsole: React.FC = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);

  const { clearHistory, clearCameraHistory, clearMapOriginHistory } = useDeveloperStore();
  const cameraHistory = useCameraHistory();
  const mapOriginHistory = useMapOriginHistory();
  const currentCamera = useCurrentCamera();
  const currentMapOrigin = useCurrentMapOrigin();
  const cameraSource = useCameraSource();
  const mapOriginSource = useMapOriginSource();

  const formatTimestamp = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatCoordinates = (value: any): string => {
    if (value.lat !== undefined && value.lng !== undefined) {
      // Camera position
      const { lat, lng, zoom, bearing, pitch } = value;
      return `lat: ${lat.toFixed(6)}, lng: ${lng.toFixed(6)}${zoom !== undefined ? `, zoom: ${zoom.toFixed(1)}` : ''}${bearing !== undefined ? `, bearing: ${bearing.toFixed(1)}°` : ''}${pitch !== undefined ? `, pitch: ${pitch.toFixed(1)}°` : ''}`;
    } else if (value.latitude !== undefined && value.longitude !== undefined) {
      // Map origin
      return `lat: ${value.latitude.toFixed(6)}, lng: ${value.longitude.toFixed(6)}`;
    }
    return JSON.stringify(value);
  };

  const getSourceColor = (source: ConfigurationSource): string => {
    switch (source) {
      case ConfigurationSource.DEFAULT_BATTERSEA:
        return '#6B7280'; // Gray
      case ConfigurationSource.ORGANIZATION:
        return '#3B82F6'; // Blue
      case ConfigurationSource.TWIN_ADMIN:
        return '#10B981'; // Green
      case ConfigurationSource.TWIN_PHYSICAL_MODEL:
        return '#8B5CF6'; // Purple
      case ConfigurationSource.SITE_BOUNDARY_CENTER:
        return '#F59E0B'; // Yellow
      case ConfigurationSource.USER_NAVIGATION:
        return '#EF4444'; // Red
      case ConfigurationSource.MAP_STORE:
        return '#EC4899'; // Pink
      case ConfigurationSource.SPECIAL_CASE:
        return '#F97316'; // Orange
      default:
        return '#6B7280';
    }
  };

  const renderConfigurationList = (
    entries: ConfigurationEntry[],
    title: string,
    clearFunction: () => void,
  ) => (
    <Section>
      <SectionHeader>
        <SectionTitle>{title}</SectionTitle>
        <ClearButton onClick={clearFunction}>Clear</ClearButton>
      </SectionHeader>
      {entries.length === 0 ? (
        <EmptyState>No {title.toLowerCase()} history</EmptyState>
      ) : (
        <ConfigList>
          {entries
            .slice()
            .reverse()
            .map((entry, index) => (
              <ConfigItem key={index}>
                <ConfigTimestamp>{formatTimestamp(entry.timestamp)}</ConfigTimestamp>
                <ConfigSource style={{ color: getSourceColor(entry.source) }}>
                  {entry.source}
                </ConfigSource>
                <ConfigValue>{formatCoordinates(entry.value)}</ConfigValue>
                {entry.details && <ConfigDetails>{entry.details}</ConfigDetails>}
              </ConfigItem>
            ))}
        </ConfigList>
      )}
    </Section>
  );

  const headerElement = (
    <Header
      onMouseEnter={() => setIsHeaderHovered(true)}
      onMouseLeave={() => setIsHeaderHovered(false)}
    >
      <HeaderLeft>
        <Title>Developer Console</Title>
        <MinimizeButton onClick={() => setIsMinimized(!isMinimized)}>
          {isMinimized ? '▲' : '▼'}
        </MinimizeButton>
      </HeaderLeft>
      <HeaderRight>
        <ClearAllButton onClick={clearHistory}>Clear All History</ClearAllButton>
      </HeaderRight>
    </Header>
  );

  return (
    <StyledDraggablePanel headerElement={headerElement} isHeaderHovered={isHeaderHovered}>
      {!isMinimized && (
        <Content>
          <CurrentStateSection>
            <SectionTitle>Current State</SectionTitle>
            <CurrentStateGrid>
              <CurrentStateItem>
                <Label>Map Origin:</Label>
                <Value>{currentMapOrigin ? formatCoordinates(currentMapOrigin) : 'Not set'}</Value>
                {mapOriginSource && (
                  <Source style={{ color: getSourceColor(mapOriginSource) }}>
                    {mapOriginSource}
                  </Source>
                )}
              </CurrentStateItem>
              <CurrentStateItem>
                <Label>Camera:</Label>
                <Value>{currentCamera ? formatCoordinates(currentCamera) : 'Not set'}</Value>
                {cameraSource && (
                  <Source style={{ color: getSourceColor(cameraSource) }}>{cameraSource}</Source>
                )}
              </CurrentStateItem>
            </CurrentStateGrid>
          </CurrentStateSection>

          {renderConfigurationList(mapOriginHistory, 'Map Origin History', clearMapOriginHistory)}
          {renderConfigurationList(cameraHistory, 'Camera History', clearCameraHistory)}
        </Content>
      )}
    </StyledDraggablePanel>
  );
};

const StyledDraggablePanel = styled(DraggablePanel)`
  width: 600px !important;
  max-height: 70vh;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
`;

const Header = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
  user-select: none;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  color: #1f2937;
  font-weight: 600;
  font-size: 14px;
`;

const MinimizeButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  font-size: 12px;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const ClearAllButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: #dc2626;
  }
`;

const Content = styled.div`
  padding: 16px;
  max-height: calc(70vh - 60px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CurrentStateSection = styled.div`
  background: rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 12px;
`;

const CurrentStateGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const CurrentStateItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Label = styled.span`
  font-weight: 600;
  color: #374151;
  font-size: 11px;
`;

const Value = styled.span`
  color: #1f2937;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 11px;
`;

const Source = styled.span`
  font-size: 10px;
  font-weight: 500;
  opacity: 0.8;
`;

const Section = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: #1f2937;
  font-weight: 600;
  font-size: 12px;
`;

const ClearButton = styled.button`
  background: #6b7280;
  color: white;
  border: none;
  padding: 3px 6px;
  border-radius: 3px;
  font-size: 10px;
  cursor: pointer;

  &:hover {
    background: #4b5563;
  }
`;

const EmptyState = styled.div`
  padding: 12px;
  text-align: center;
  color: #6b7280;
  font-style: italic;
  font-size: 11px;
`;

const ConfigList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const ConfigItem = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ConfigTimestamp = styled.span`
  font-size: 10px;
  color: #6b7280;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
`;

const ConfigSource = styled.span`
  font-size: 10px;
  font-weight: 600;
`;

const ConfigValue = styled.span`
  font-size: 11px;
  color: #1f2937;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
`;

const ConfigDetails = styled.span`
  font-size: 10px;
  color: #6b7280;
  font-style: italic;
`;

export default DeveloperConsole;
